'use client';

import { useState } from 'react';
import { FieldValues, Path, useController } from 'react-hook-form';
import { AbstractControl } from '../abstract-control';
import DallasFormField from '../DallasFormField/DallasFormField';
import handleKeyDown from '~/app/_ui/shared/handle-keydown';
import { toCommaSeparated } from '~/utils/number';
import { toPluralized } from '~/utils/pluralize';
import useDidChange from '~/app/_ui/useDidChange';

export interface Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> extends AbstractControl<TName, number | null> {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  maxFractionalDigits?: 0 | 1 | 2;
  commaSeparated?: boolean;
  step?: number;
  textAlignment?: 'start' | 'end';
  addOnStart?: React.ReactElement;
  addOnEnd?: React.ReactElement;
  footnoteStart?: React.ReactElement;
  errorMessages?: {
    min?: string;
    max?: string;
    minLength?: string;
    maxLength?: string;
  };
}

export default function DallasNumberField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  className,
  label,
  name,
  placeholder,
  addOnStart,
  addOnEnd,
  textAlignment = 'start',
  footnoteStart,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  minLength,
  maxLength,
  required,
  disabled,
  maxFractionalDigits = 0,
  step = 1,
  commaSeparated: isCommaSeparated = false,
  onChange: onValueChange,
  errorPlacement,
  errorMessages,
}: Props<TFieldValues, TName>) {
  const [formattedValue, setFormattedValue] = useState<string>('');
  const {
    field: { ref, value, onChange: setValue, onBlur },
    fieldState: { error },
  } = useController({
    name,
    rules: {
      required: required
        ? typeof required === 'string'
          ? required
          : 'Required field'
        : false,
      min,
      max,
      validate: {
        minLength: (value: number | null) => {
          if (value === null || !minLength) {
            return true;
          }
          return value.toString().length >= minLength;
        },
        maxLength: (value: number | null) => {
          if (value === null || !maxLength) {
            return true;
          }
          return value.toString().length <= maxLength;
        },
      },
    },
  });
  const didValueChange = useDidChange(value, { initialCompareValue: null });
  const didIsCommaSeparatedChange = useDidChange(isCommaSeparated);

  if (didValueChange) {
    setFormattedValue(
      value === null ? '' : isCommaSeparated ? toCommaSeparated(value) : value,
    );
  }

  if (didIsCommaSeparatedChange) {
    setFormattedValue(isCommaSeparated ? toCommaSeparated(value) : value);
  }

  function onChange(input: string) {
    const isEmpty = input === '';
    const isSubtractSign = input === '-';

    if (isEmpty || isSubtractSign) {
      setFormattedValue(input);

      if (value !== null) {
        setValue(null);
        onValueChange?.(null);
      }

      return;
    }

    const lastIndex = input.length - 1;
    const hasTrailingDecimalPoint = input[lastIndex] === '.';
    const decimalPointIndex = input.indexOf('.');
    const fractionalDigitsCount =
      decimalPointIndex === -1 ? 0 : input.length - 1 - decimalPointIndex;

    if (
      (hasTrailingDecimalPoint && maxFractionalDigits === 0) ||
      (hasTrailingDecimalPoint && decimalPointIndex !== lastIndex) ||
      fractionalDigitsCount > maxFractionalDigits
    ) {
      return;
    }

    const isNegative = input[0] === '-';
    const [integer, fractionalDigits = ''] = input
      .replace(/[^0-9|\.]/g, '')
      .split('.');
    const absoluteIntegerValue = +integer;
    const integerValue = isNegative
      ? -absoluteIntegerValue
      : absoluteIntegerValue;

    const fractionalValue = parseFloat(`0.${fractionalDigits || '0'}`);
    const wholeValue = isNegative
      ? integerValue - fractionalValue
      : integerValue + fractionalValue;

    setFormattedValue(
      // Using integerValue in order to retain trailing fractional 0's
      `${isCommaSeparated ? toCommaSeparated(integerValue) : integerValue}${
        hasTrailingDecimalPoint && decimalPointIndex === lastIndex
          ? '.'
          : fractionalDigits.length
          ? `.${fractionalDigits}`
          : ''
      }`,
    );

    setValue(wholeValue);
    onValueChange?.(wholeValue);
  }

  function increment() {
    const incrementedValue = (value ?? 0) + step;

    onChange(toCommaSeparated(incrementedValue));
  }

  function decrement() {
    const decrementedValue = (value ?? 0) - step;

    onChange(toCommaSeparated(decrementedValue));
  }

  return (
    <DallasFormField
      className={className}
      label={label}
      error={error}
      errorPlacement={errorPlacement}
      errorMessages={{
        min: min
          ? errorMessages?.min ??
            `Must be at least ${isCommaSeparated ? toCommaSeparated(min) : min}`
          : undefined,
        max: max
          ? errorMessages?.max ??
            `Must not be more than ${
              isCommaSeparated ? toCommaSeparated(max) : max
            }`
          : undefined,
        minLength: minLength
          ? errorMessages?.minLength ??
            `At least ${minLength} ${toPluralized(minLength, 'digit')}`
          : undefined,
        maxLength: maxLength
          ? errorMessages?.maxLength ??
            `Maximum ${maxLength} ${toPluralized(maxLength, 'digit')}`
          : undefined,
      }}
      required={!!required}
      addOnStart={addOnStart}
      addOnEnd={addOnEnd}
      footnoteStart={footnoteStart}
    >
      <input
        ref={ref}
        style={
          textAlignment === 'end'
            ? {
                textAlign: 'right',
              }
            : undefined
        }
        name={name}
        type="text"
        placeholder={placeholder}
        onBlur={onBlur}
        value={formattedValue}
        onChange={({ target: { value } }) => onChange(value.trim())}
        onKeyDown={handleKeyDown({
          onKeyDownArrowUp: increment,
          onKeyDownArrowDown: decrement,
        })}
        aria-invalid={!!error}
        disabled={disabled}
        autoComplete="off"
      />
    </DallasFormField>
  );
}
